import React, { useState, useRef, useEffect } from 'react';
import { X, ChevronDown } from 'lucide-react';
import CalendarRangeInput from './CalendarRangeInput';

// Export drawer for the platform order tables (ERP-5041).
//
// Separate from FilterDrawer because it confirms with a single Export action and carries
// the default-behaviour note under it, rather than the Clear/Cancel/Apply footer. The
// fields below deliberately mirror FilterDrawer's "simpleList" markup — same chip
// container, yellow chips, rotating chevron, checked-row tick — so the two drawers read
// as one control set even though their footers differ.

const CHIP_YELLOW = '#FFEB00';

const LABEL = {
    display: 'block',
    fontFamily: 'Montserrat',
    fontSize: 12,
    fontWeight: 600,
    color: '#212529',
    marginBottom: 6,
};

/**
 * Multi-select matching FilterDrawer's simpleList. Pass `searchable` for the
 * "Search and Select..." variant, which adds a type-to-filter input inline with the chips.
 */
function SelectField({
    options,
    selected,
    onChange,
    placeholder,
    searchable = false,
    isOpen,
    onToggle,
    onClose,
}) {
    const wrapperRef = useRef(null);
    const [query, setQuery] = useState('');

    useEffect(() => {
        if (!isOpen) return;
        const onDocDown = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) onClose();
        };
        document.addEventListener('mousedown', onDocDown);
        return () => document.removeEventListener('mousedown', onDocDown);
    }, [isOpen, onClose]);

    useEffect(() => { if (!isOpen) setQuery(''); }, [isOpen]);

    const toggle = (value) => {
        onChange(selected.includes(value)
            ? selected.filter(v => v !== value)
            : [...selected, value]);
    };

    const visible = query
        ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
        : options;

    return (
        <div ref={wrapperRef} style={{ position: 'relative', marginTop: 6 }}>
            <div
                style={{
                    width: '100%',
                    minHeight: 44,
                    borderRadius: 8,
                    border: '1px solid #6C757D',
                    padding: '6px 30px 6px 10px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 6,
                    background: '#fff',
                    cursor: 'pointer',
                    position: 'relative',
                }}
                onMouseDown={(e) => { e.stopPropagation(); onToggle(); }}
            >
                {selected.map(value => (
                    <span
                        key={value}
                        style={{
                            background: CHIP_YELLOW,
                            color: '#212529',
                            borderRadius: 16,
                            padding: '4px 10px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            fontSize: 12,
                            fontWeight: 600,
                            fontFamily: 'Montserrat',
                        }}
                    >
                        {value}
                        <button
                            type="button"
                            onMouseDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); onChange(selected.filter(v => v !== value)); }}
                            style={{ background: 'none', border: 'none', marginLeft: 6, cursor: 'pointer', padding: 0, display: 'flex' }}
                        >
                            <X size={14} strokeWidth={3} />
                        </button>
                    </span>
                ))}

                {searchable && isOpen ? (
                    <input
                        autoFocus
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        placeholder={selected.length ? '' : placeholder}
                        style={{
                            flex: 1,
                            minWidth: 80,
                            border: 'none',
                            outline: 'none',
                            fontSize: 14,
                            fontFamily: 'Montserrat',
                            color: '#212529',
                            background: 'transparent',
                        }}
                    />
                ) : selected.length === 0 && (
                    <span style={{ color: '#6C757D', fontSize: 14, fontFamily: 'Montserrat' }}>
                        {placeholder}
                    </span>
                )}

                <ChevronDown
                    size={16}
                    style={{
                        position: 'absolute',
                        right: 10,
                        top: '50%',
                        transform: isOpen ? 'translateY(-50%) rotate(180deg)' : 'translateY(-50%)',
                        color: '#6C757D',
                        transition: 'transform 0.2s ease',
                        cursor: 'pointer',
                    }}
                />
            </div>

            {isOpen && (
                <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        position: 'absolute',
                        top: 'calc(100% + 4px)',
                        left: 0,
                        right: 0,
                        border: '1px solid #e9ecef',
                        borderRadius: 8,
                        maxHeight: 220,
                        overflowY: 'auto',
                        backgroundColor: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        zIndex: 9999,
                    }}
                >
                    {visible.length === 0 ? (
                        <div style={{ padding: 10, fontSize: 14, fontFamily: 'Montserrat', color: '#6C757D' }}>
                            {options.length === 0 ? 'No options available' : 'No matches'}
                        </div>
                    ) : visible.map(value => {
                        const checked = selected.includes(value);
                        return (
                            <div
                                key={value}
                                onClick={() => toggle(value)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    padding: 10,
                                    cursor: 'pointer',
                                    fontSize: 14,
                                    fontFamily: 'Montserrat',
                                    backgroundColor: checked ? '#FFFEF0' : 'transparent',
                                }}
                            >
                                <span style={{ color: '#212529', fontWeight: 400 }}>{value}</span>
                                {checked && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M16.6663 5L7.49967 14.1667L3.33301 10" stroke="#6C757D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default function ExportOrdersDrawer({
    isOpen,
    onClose,
    values,
    setValues,
    statusOptions = [],
    branchOptions = [],
    fulfillmentOptions = [],
    onExport,
    isExporting = false,
}) {
    const [openField, setOpenField] = useState(null);

    useEffect(() => { if (!isOpen) setOpenField(null); }, [isOpen]);

    if (!isOpen) return null;

    const set = (key, v) => setValues(prev => ({ ...prev, [key]: v }));
    const fieldProps = (key) => ({
        isOpen: openField === key,
        onToggle: () => setOpenField(prev => (prev === key ? null : key)),
        onClose: () => setOpenField(prev => (prev === key ? null : prev)),
    });

    return (
        <>
            <div className="drawer-overlay" onClick={onClose} />
            <div className="entity-drawer" style={{ width: 400 }}>
                <div className="drawer-header" style={{ backgroundColor: '#F8F9FA', padding: '15px 20px' }}>
                    <h3 style={{ margin: 0 }}>Export orders</h3>
                    <button className="close-btn" onClick={onClose}><X size={18} /></button>
                </div>

                <div
                    className="main-drawer-inner"
                    style={{ padding: '15px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}
                >
                    <div style={{ marginBottom: 14 }}>
                        <label style={LABEL}>Order Time</label>
                        <CalendarRangeInput
                            name="exportOrderTime"
                            enableTime
                            startDate={values.order_time_start || null}
                            endDate={values.order_time_end || null}
                            onChange={(e) => setValues(prev => ({
                                ...prev,
                                order_time_start: e.target.value.start,
                                order_time_end: e.target.value.end,
                            }))}
                        />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <label style={LABEL}>Order Status</label>
                        <SelectField
                            placeholder="Select..."
                            options={statusOptions}
                            selected={values.order_status || []}
                            onChange={(v) => set('order_status', v)}
                            {...fieldProps('status')}
                        />
                    </div>

                    <div style={{ marginBottom: 14 }}>
                        <label style={LABEL}>Linked Branch</label>
                        <SelectField
                            searchable
                            placeholder="Search and Select..."
                            options={branchOptions}
                            selected={values.linked_branch || []}
                            onChange={(v) => set('linked_branch', v)}
                            {...fieldProps('branch')}
                        />
                    </div>

                    <div style={{ marginBottom: 18 }}>
                        <label style={LABEL}>Fulfillment Method</label>
                        <SelectField
                            placeholder="Select..."
                            options={fulfillmentOptions}
                            selected={values.fulfillment_method || []}
                            onChange={(v) => set('fulfillment_method', v)}
                            {...fieldProps('fulfillment')}
                        />
                    </div>

                    <button
                        type="button"
                        onClick={onExport}
                        disabled={isExporting}
                        style={{
                            width: '100%',
                            height: 40,
                            borderRadius: 8,
                            border: 'none',
                            background: '#212529',
                            color: '#fff',
                            fontFamily: 'Montserrat',
                            fontSize: 13,
                            fontWeight: 600,
                            cursor: isExporting ? 'not-allowed' : 'pointer',
                            opacity: isExporting ? 0.6 : 1,
                        }}
                    >
                        {isExporting ? 'Exporting…' : 'Export'}
                    </button>

                    <p style={{
                        margin: '10px 0 0',
                        fontFamily: 'Montserrat',
                        fontSize: 12,
                        lineHeight: 1.5,
                        color: '#6C757D',
                    }}>
                        If you don't set any filters, orders in all statuses from today will be
                        exported by default.
                    </p>

                    {/* Dismiss without exporting. Styled as FilterDrawer's Cancel so the two
                        drawers offer the same secondary action. marginTop:auto pins it to the
                        bottom of the panel instead of trailing straight after the note. */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 'auto', paddingTop: 24 }}>
                        <button
                            type="button"
                            onClick={onClose}
                            style={{
                                display: 'flex',
                                height: 34,
                                minWidth: 100,
                                padding: 10,
                                justifyContent: 'center',
                                alignItems: 'center',
                                gap: 10,
                                borderRadius: 8,
                                background: 'var(--Background-White, #FFF)',
                                border: '1px solid var(--navbar-dropdown-active, #212529)',
                                color: 'var(--navbar-dropdown-active, #212529)',
                                boxShadow: '0 1px 4px 0 rgba(0, 0, 0, 0.10)',
                                fontFamily: 'Montserrat',
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: 'pointer',
                            }}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
}
